import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface BiProps {
    hi: ReactNode
    en: ReactNode
    /** stack: Hindi above English. inline: "हिन्दी · English" (stacks on small screens). */
    layout?: 'stack' | 'inline'
    className?: string
    hiClassName?: string
    enClassName?: string
    /** Always show both lines, ignoring the explanation-language setting (UI chrome). */
    always?: boolean
}

/**
 * Hindi + English explanation text. The explanation-language setting (हिन्दी / English / दोनों) hides one of them
 * through data-ex attributes, before paint, without re-rendering.
 */
export const Bi: FC<BiProps> = ({
    hi,
    en,
    layout = 'stack',
    className,
    hiClassName,
    enClassName,
    always = false
}): ReactNode => {
    const exHi = always ? undefined : 'hi'
    const exEn = always ? undefined : 'en'

    if (layout === 'inline') {
        return (
            <span className={cn('inline-flex flex-wrap items-baseline gap-x-1.5', className)}>
                <span lang='hi' data-ex={exHi} className={cn('hindi', hiClassName)}>{hi}</span>
                <span aria-hidden='true' className='ex-sep text-muted-foreground/70'>·</span>
                <span lang='en' data-ex={exEn} className={enClassName}>{en}</span>
            </span>
        )
    }

    return (
        <span className={cn('flex flex-col', className)}>
            <span lang='hi' data-ex={exHi} className={cn('hindi', hiClassName)}>{hi}</span>
            <span lang='en' data-ex={exEn} className={cn('text-muted-foreground', enClassName)}>{en}</span>
        </span>
    )
}

/** Two-line navigation label: Hindi above, English below (always both). */
export const NavLabel: FC<{ hi: string, en: string, className?: string }> = ({ hi, en, className }): ReactNode => (
    <span className={cn('flex flex-col items-center leading-tight', className)}>
        <span lang='hi' className='hindi text-[0.9375rem] font-medium'>{hi}</span>
        <span lang='en' className='text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-muted-foreground'>{en}</span>
    </span>
)

/** A small eyebrow: Devanagari word over an English heading, like the reference's section heads. */
export const Eyebrow: FC<{ children: ReactNode, className?: string }> = ({ children, className }): ReactNode => (
    <p lang='hi' className={cn('literary text-base tracking-wide text-saffron-strong', className)}>{children}</p>
)
